import { useMemo, useState } from 'react'
import {
  BookmarkIcon,
  BookOpenIcon,
  ChatBubbleLeftEllipsisIcon,
  ClipboardDocumentListIcon,
  ClockIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  HandRaisedIcon,
} from '@heroicons/react/24/outline'
import contentTypeUtils from '../contentTypeUtils.js'
import { classifyPublishFailure } from '../publishingReliability.js'
import { getPublishReadiness } from '../studioStatusUtils.js'
import { AutonomyCommandStrip } from './AutonomyCommandStrip.jsx'
import { EngagementInsightsSummary } from './EngagementInsightsSummary.jsx'
import { FollowerGrowthCard } from './FollowerGrowthCard.jsx'
import { normalizeScore } from './authorityTopicUtils.js'
import { resolveDashboardBottleneck } from './dashboardBottleneck.js'
import { useAutonomySnapshot } from './hooks/useAutonomySnapshot.ts'
import { PageHeader, LifecycleStrip } from './ui-kit/index.js'

// Map each resume-item kind to a glanceable status icon. The kinds
// come from buildResumeItems in studioDashboardUtils.js. Keeping this
// in one place so adding a new kind there + an icon here stays a
// two-line change. Defaults to BookmarkIcon for the topic-candidate
// "review" action and any future kind.
function getResumeItemIcon(kind) {
  switch (kind) {
    case 'overdue-planned':
    case 'due-today':
    case 'scheduled':
      return ClockIcon
    case 'publish-failure':
    case 'sync-failure':
      return ExclamationTriangleIcon
    case 'checklist-incomplete':
      return ClipboardDocumentListIcon
    case 'delivered-manually':
      return HandRaisedIcon
    case 'feedback-needed':
      return ChatBubbleLeftEllipsisIcon
    case 'draft':
    case 'converted-capture':
      return DocumentTextIcon
    case 'brief':
      return BookOpenIcon
    default:
      return BookmarkIcon
  }
}

// Footer mode chip ordering — operator scans left-to-right, so the
// content types most likely to be on `auto` come first. Mirrors the
// canonical registry order in content_types.json.
const FOOTER_MODE_TYPES = [
  { key: 'linkedin-post', short: 'li-post' },
  { key: 'x-post', short: 'x-post' },
  { key: 'linkedin-article', short: 'li-article' },
  { key: 'newsletter-issue', short: 'newsletter' },
]

const { getContentType, getLengthMetrics } = contentTypeUtils

const PILLAR_SHORT = {
  'system-decision': 'System',
  'build-log': 'Build',
  'deep-dive': 'Technical',
  'engineering-standards': 'Engineering',
}

const PILLAR_TONES = {
  'system-decision': 'gold',
  'build-log': 'blue',
  'deep-dive': 'violet',
  'engineering-standards': 'green',
}

const WEEK_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

function buildAttentionItems({
  dashboard,
  runtimeVerifications,
  integrationEntries,
  fmtDateTime,
}) {
  const items = []

  if (dashboard.counts.publishFailed > 0) {
    items.push({
      key: 'publish-failures',
      label: 'Publish failures',
      summary: `${dashboard.counts.publishFailed} item${dashboard.counts.publishFailed === 1 ? '' : 's'} need recovery`,
      tone: 'error',
      tab: 'drafts',
    })
  }

  if (dashboard.counts.syncFailed > 0) {
    items.push({
      key: 'sync-failures',
      label: 'Sync failures',
      summary: `${dashboard.counts.syncFailed} item${dashboard.counts.syncFailed === 1 ? '' : 's'} blocked on Notion sync`,
      tone: 'error',
      tab: 'drafts',
    })
  }

  const verificationFailure = runtimeVerifications.find(
    (item) => item.status === 'failure'
  )
  if (verificationFailure) {
    items.push({
      key: `verification:${verificationFailure.id}`,
      label: 'Verification warning',
      summary:
        verificationFailure.errorCode ||
        fmtDateTime(
          verificationFailure.finishedAt || verificationFailure.startedAt
        ),
      tone: 'warning',
      tab: 'settings',
    })
  }

  integrationEntries
    .filter((entry) => !entry.available || entry.status === 'unsupported')
    .slice(0, 2)
    .forEach((entry) => {
      items.push({
        key: `integration:${entry.key}`,
        label: entry.label,
        summary:
          entry.status === 'unsupported'
            ? 'Unsupported or quarantined'
            : 'Needs configuration before live use',
        tone: entry.status === 'unsupported' ? 'error' : 'muted',
        tab: 'settings',
      })
    })

  return items.slice(0, 5)
}

function classifyRecoveryItem({ post, attempt, integrationHealth }) {
  const type = getContentType(post?.contentType)
  const readiness = getPublishReadiness({ type, post, integrationHealth })
  const lengthMetrics = getLengthMetrics(type.key, post?.content || '')
  return classifyPublishFailure({
    latestAttempt: attempt,
    publishReadiness: readiness,
    lengthMetrics,
  }).recoveryState
}

function buildPublishRecoveryItems({
  posts = [],
  publishAttempts = [],
  integrationHealth = null,
}) {
  const postsById = new Map(posts.map((post) => [post.id, post]))
  return publishAttempts
    .filter((attempt) => attempt.outcome === 'failure' && attempt.postId)
    .map((attempt) => {
      const post = postsById.get(attempt.postId)
      const actionTab =
        post?.status === 'published'
          ? 'delivered'
          : post?.status === 'scheduled'
            ? 'queue'
            : 'drafts'
      return {
        id: `publish-recovery:${attempt.id || attempt.postId}`,
        actionTab,
        targetId: attempt.postId,
        title: post?.topic || attempt.targetPlatform || 'Failed publish',
        platform: attempt.targetPlatform || 'Live API',
        provider:
          attempt.targetPlatform === 'X' ||
          getContentType(post?.contentType).platform === 'x'
            ? 'x'
            : 'linkedin',
        errorCode: attempt.errorCode || 'PUBLISH_FAILED',
        attemptedAt: attempt.updatedAt || attempt.createdAt,
        recoveryState: classifyRecoveryItem({
          post,
          attempt,
          integrationHealth,
        }),
      }
    })
}

function formatDateLine(date = new Date()) {
  return date.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

function formatShortTime(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  })
}

function getCurrentWeekDates() {
  const today = new Date()
  const day = today.getDay() || 7
  const monday = new Date(today)
  monday.setHours(0, 0, 0, 0)
  monday.setDate(today.getDate() - day + 1)
  return WEEK_DAYS.map((label, index) => {
    const date = new Date(monday)
    date.setDate(monday.getDate() + index)
    return { label, date, iso: date.toISOString().slice(0, 10) }
  })
}

function getDayPillar(dayIndex) {
  if (dayIndex === 0 || dayIndex === 6) return 'system-decision'
  if (dayIndex === 1 || dayIndex === 4) return 'build-log'
  if (dayIndex === 2) return 'deep-dive'
  if (dayIndex === 3) return 'engineering-standards'
  return null
}

function getPillarName(id, getPillar) {
  if (!id) return 'Rest'
  const pillar = getPillar?.(id)
  return PILLAR_SHORT[id] || pillar?.label || id
}

function getPostDate(post) {
  return (
    post.scheduledFor ||
    post.publishedAt ||
    post.updatedAt ||
    post.createdAt ||
    ''
  )
}

function buildWeekCards(posts, getPillar) {
  const cards = getCurrentWeekDates()
  const postsByDate = new Map()
  posts.forEach((post) => {
    const iso = getPostDate(post)?.slice(0, 10)
    if (!iso || postsByDate.has(iso)) return
    postsByDate.set(iso, post)
  })

  const todayIso = new Date().toISOString().slice(0, 10)
  return cards.map((card, index) => {
    const post = postsByDate.get(card.iso)
    const pillarId = post?.pillar || getDayPillar(index)
    const status = post?.status || (pillarId ? 'planned' : 'rest')
    return {
      ...card,
      post,
      today: card.iso === todayIso,
      pillarId,
      pillarLabel: getPillarName(pillarId, getPillar),
      tone: PILLAR_TONES[pillarId] || 'muted',
      status,
      title:
        post?.topic ||
        (pillarId ? `${getPillarName(pillarId, getPillar)} slot` : 'Rest bank'),
    }
  })
}

function buildArcSegments(arcDay) {
  return Array.from({ length: 90 }, (_, index) => {
    const day = index + 1
    const phase = day <= 30 ? 'foundation' : day <= 60 ? 'depth' : 'expansion'
    const active = arcDay ? day <= arcDay : day <= 1
    const today = arcDay === day
    const isGap = day % 13 === 0 || day % 29 === 0
    return { day, phase, active, today, isGap }
  })
}

function buildActivityLog({
  publishAttempts,
  runtimeVerifications,
  fmtDateTime,
}) {
  const publishRows = publishAttempts.slice(0, 3).map((attempt) => ({
    id: `attempt:${attempt.id}`,
    level: attempt.outcome === 'success' ? 'INFO' : 'ERR',
    action: attempt.outcome === 'success' ? 'ship' : 'publish',
    detail:
      attempt.errorCode ||
      attempt.externalRefId ||
      `${attempt.targetPlatform} attempt recorded`,
    time: fmtDateTime(attempt.updatedAt || attempt.createdAt),
  }))

  const verificationRows = runtimeVerifications.slice(0, 3).map((check) => ({
    id: `verification:${check.id}`,
    level: check.status === 'failure' ? 'WARN' : 'INFO',
    action: 'verify',
    detail: check.errorCode || check.checkKey,
    time: fmtDateTime(check.finishedAt || check.startedAt || check.createdAt),
  }))

  return [...publishRows, ...verificationRows].slice(0, 5)
}

function ConsoleBadge({ tone = 'muted', children }) {
  return <span className={`studio-console-badge ${tone}`}>{children}</span>
}

function SectionDivider({ title, meta, right }) {
  return (
    <div className="studio-section-divider">
      <div>
        <strong>{title}</strong>
        {meta ? <em>{meta}</em> : null}
      </div>
      {right ? <small>{right}</small> : null}
    </div>
  )
}

function ProgressRail({ arcDay }) {
  const segments = buildArcSegments(arcDay)
  return (
    <div className="studio-arc">
      <div className="studio-arc-meta">
        <span>arc</span>
        {/* UI elevation 2026-06-09: show "Arc not started" instead of bare "--/90" */}
        <span>{arcDay ? `day ${arcDay}/90` : 'Arc not started'}</span>
        <span>
          phase:{' '}
          {arcDay && arcDay > 60
            ? 'authority layer'
            : arcDay && arcDay > 30
              ? 'add depth'
              : 'establish rhythm'}
        </span>
        <strong>{Math.max(0, Math.min(90, arcDay || 0))} active days</strong>
      </div>
      <div className="studio-arc-track" aria-label="90 day arc progress">
        {segments.map((segment) => (
          <span
            key={segment.day}
            className={[
              'studio-arc-segment',
              segment.phase,
              segment.active ? 'active' : '',
              segment.today ? 'today' : '',
              segment.isGap ? 'gap' : '',
            ].join(' ')}
            title={`Day ${segment.day}`}
          />
        ))}
      </div>
    </div>
  )
}

function MetricCard({ label, value, meta, onClick }) {
  const isZero = value === 0 || value === '0'
  return (
    <button className="studio-card metric-card" onClick={onClick}>
      <div className="metric-label">{label}</div>
      <div className={`metric-value ${isZero ? 'is-zero' : ''}`}>{value}</div>
      {meta ? <div className="metric-meta">{meta}</div> : null}
    </button>
  )
}

function MetricsGrid({ items }) {
  return (
    <div className="studio-metrics-grid">
      {items.map((item) => (
        <MetricCard
          key={item.label}
          label={item.label}
          value={item.value}
          meta={item.meta}
          onClick={item.onClick}
        />
      ))}
    </div>
  )
}

function WeekCard({ item, onOpen }) {
  return (
    <button
      className={`studio-week-card ${item.tone} ${item.today ? 'today' : ''}`}
      onClick={onOpen}
    >
      <div className="studio-week-head">
        <span>{item.label}</span>
        <small>{item.date.getDate()}</small>
      </div>
      <div className="studio-week-pillar">
        {item.pillarId
          ? `0${['system-decision', 'build-log', 'deep-dive', 'engineering-standards'].indexOf(item.pillarId) + 1}`
          : '--'}{' '}
        · {item.pillarLabel}
      </div>
      <p>{item.title}</p>
      <div className="studio-week-foot">
        <span>{item.status}</span>
        <small>
          {item.post?.contentType
            ? getContentType(item.post.contentType).platformLabel ||
              getContentType(item.post.contentType).label
            : 'slot'}
        </small>
      </div>
    </button>
  )
}

export function DashboardView({
  dashboard,
  posts = [],
  ideas = [],
  publishAttempts,
  runtimeVerifications,
  integrationHealth,
  startDate,
  authorityReview,
  authorityReviews = [],
  authorityTopicCandidates = [],
  authorityCoverage = null,
  strategicAssetCoverage = null,
  authorityReviewLoading,
  onRunAuthorityReview,
  onOpenResumeItem,
  onOpenTab,
  fmtDateTime,
  getPillar,
  RUNTIME_CHECK_LABELS,
  btn,
}) {
  const [publishRecoveryFilter, setPublishRecoveryFilter] = useState('all')
  // One snapshot drives both the AutonomyCommandStrip cards above and
  // the footer chips below. Lifting the hook from the strip to the
  // view eliminated a duplicate fetch and made the footer policy chips
  // possible — the kill-switch chip and per-type mode chips now reflect
  // live state instead of the hardcoded fixtures that were committed
  // when this footer was first scaffolded.
  const autonomySnapshot = useAutonomySnapshot({
    authorityTopicCandidates,
    posts,
  })
  const integrationEntries = Object.values(
    integrationHealth?.integrations || {}
  )
  const readyIntegrations = integrationEntries.filter((item) => item.available)
  const primaryAction = dashboard.resumeItems[0] || null
  const primaryPost = primaryAction?.targetId
    ? posts.find((post) => post.id === primaryAction.targetId)
    : null
  const primaryType = getContentType(primaryPost?.contentType)
  const primaryMetrics = getLengthMetrics(
    primaryType.key,
    primaryPost?.content || ''
  )
  const attentionItems = buildAttentionItems({
    dashboard,
    runtimeVerifications,
    integrationEntries,
    fmtDateTime,
  })
  const publishRecoveryItems = buildPublishRecoveryItems({
    posts,
    publishAttempts,
    integrationHealth,
  })
  const filteredRecoveryItems = publishRecoveryItems.filter((item) => {
    if (publishRecoveryFilter === 'all') return true
    if (publishRecoveryFilter === 'linkedin')
      return item.provider === 'linkedin'
    if (publishRecoveryFilter === 'x') return item.provider === 'x'
    return item.recoveryState === publishRecoveryFilter
  })
  const authority = dashboard.authority || {
    missingAngles: [],
    strongAngles: [],
    pillarBalance: {},
    nextAction: '',
  }
  const pendingTopics = authorityTopicCandidates
    .filter((item) => item.status === 'pending' || item.status === 'accepted')
    .slice(0, 3)
  const latestDurableAuthorityReview = authorityReviews[0] || null
  const strongProofAssets =
    strategicAssetCoverage?.strong || dashboard.counts.strongProofAssets || 0
  const unlinkedProofAssets =
    strategicAssetCoverage?.unlinked ||
    dashboard.counts.unlinkedProofAssets ||
    0
  const unusedProofAssets =
    strategicAssetCoverage?.unused || dashboard.counts.unusedProofAssets || 0

  const arcDay = startDate
    ? Math.min(
        90,
        Math.max(
          1,
          Math.floor((Date.now() - new Date(startDate).getTime()) / 86400000) +
            1
        )
      )
    : null
  const failureCount =
    dashboard.counts.publishFailed + dashboard.counts.syncFailed
  const currentPillarId = getDayPillar((new Date().getDay() + 6) % 7)
  const weekCards = useMemo(
    () => buildWeekCards(posts, getPillar),
    [posts, getPillar]
  )
  const activityLog = buildActivityLog({
    publishAttempts,
    runtimeVerifications,
    fmtDateTime,
  })
  const actTwoMetrics = [
    {
      label: 'Captures',
      value: dashboard.counts.ideas,
      meta: `${dashboard.counts.ideas ? 'raw ideas' : 'empty inbox'}`,
      onClick: () => onOpenTab('ideas'),
    },
    {
      label: 'Proof',
      value: dashboard.counts.proofAssets,
      meta: `${unlinkedProofAssets} unlinked`,
      onClick: () => onOpenTab('proof'),
    },
    {
      label: 'Drafts',
      value: dashboard.counts.drafts,
      meta: failureCount ? `${failureCount} blocked` : 'ready stack',
      onClick: () => onOpenTab('drafts'),
    },
    {
      label: 'Queued',
      value: dashboard.counts.queue,
      meta: dashboard.nextPlannedItem?.scheduledFor
        ? `next ${formatShortTime(dashboard.nextPlannedItem.scheduledFor)}`
        : 'no slot',
      onClick: () => onOpenTab('queue'),
    },
    {
      label: 'Published',
      value: dashboard.counts.delivered,
      meta: 'arc total',
      onClick: () => onOpenTab('delivered'),
    },
    {
      label: 'Failed',
      value: failureCount,
      meta: failureCount ? 'needs action' : 'clear',
      onClick: () => onOpenTab(failureCount ? 'drafts' : 'settings'),
    },
  ]

  const stackItems = [
    ...dashboard.resumeItems.slice(1, 4).map((item, index) => ({
      key: item.id,
      number: String(index + 2).padStart(2, '0'),
      title: item.title,
      meta: item.meta || item.label,
      action: item.label,
      Icon: getResumeItemIcon(item.kind),
      onClick: () => onOpenResumeItem(item),
    })),
    ...pendingTopics.slice(0, 2).map((item, index) => ({
      key: item.id,
      number: String(index + dashboard.resumeItems.length + 1).padStart(2, '0'),
      title: item.title,
      // normalizeScore handles both schemas (0–100 integer from
      // topicDiscovery/topicAutoPromoter, 0–1 fraction from
      // proofAssetsService) so the operator never sees "8300%" or
      // "score 1%" depending on which producer minted the candidate.
      meta: `${item.pillar} · score ${normalizeScore(item.score)}`,
      action: 'review',
      Icon: BookmarkIcon,
      onClick: () => onOpenTab('ideas'),
    })),
  ].slice(0, 4)

  // Wave Q.3 — operational page header + lifecycle strip.
  //
  // The bottleneck heuristic walks the lifecycle in order: the first
  // stage with zero useful work becomes the bottleneck. Order matters
  // — the lifecycle is a feeder pipeline, so the earliest empty stage
  // is the one that gates everything downstream.
  const lifecycleCounts = {
    ideas: ideas.length,
    truth:
      (authority?.strongAngles?.length || 0) +
      (dashboard?.counts?.truthPositions || 0),
    research: dashboard?.counts?.researchBriefs || 0,
    proof: strategicAssetCoverage?.strong || 0,
    create: posts.filter((p) => p.status === 'draft').length,
    queue: posts.filter((p) => p.status === 'scheduled').length,
    delivered: posts.filter((p) => p.status === 'published').length,
  }
  // C4 (2026-05-09) — centralized resolver. The Dashboard's bottleneck
  // is the only surface that needs whole-pipeline visibility (per-tab
  // bottlenecks correctly describe their local concerns). Ordered
  // priority: kill switch → publish failures → open incidents →
  // integration health → defenseless drafts → lifecycle-empty stages.
  // See src/dashboardBottleneck.js for the full ordering rationale.
  const bottleneck = resolveDashboardBottleneck({
    autonomyState: autonomySnapshot.state,
    publishFailedCount: dashboard?.counts?.publishFailed || 0,
    incidents: autonomySnapshot.incidents || [],
    integrationHealth,
    truthCount: lifecycleCounts.truth,
    proofCount: lifecycleCounts.proof,
    draftsInMotion: lifecycleCounts.create + lifecycleCounts.queue,
    ideas: lifecycleCounts.ideas,
    truth: lifecycleCounts.truth,
    proof: lifecycleCounts.proof,
    drafts: lifecycleCounts.create,
    queue: lifecycleCounts.queue,
    acceptedTopicsWithoutProof: authorityTopicCandidates.filter(
      (t) => t.status === 'accepted'
    ).length,
  })
  const arcMeta = arcDay
    ? `Arc day ${arcDay}/90`
    : startDate
      ? 'Arc pending'
      : 'No arc set'
  const headerMeta = [
    arcMeta,
    `${lifecycleCounts.create + lifecycleCounts.queue} in motion`,
    `${lifecycleCounts.proof} proof assets`,
    readyIntegrations.length
      ? `${readyIntegrations.length} integrations ready`
      : 'No integrations',
  ]
  const lifecycleBlocked = {
    ideas: lifecycleCounts.ideas === 0,
    truth: lifecycleCounts.truth === 0,
    proof: lifecycleCounts.proof === 0,
  }

  return (
    <div className="studio-operator-dashboard">
      <PageHeader
        eyebrow="Command"
        title="Today's operating state"
        subtitle="Turn signal, evidence, and decisions into proof-backed public authority. The lifecycle below shows where work is stuck and what unblocks it."
        meta={headerMeta}
        bottleneck={bottleneck}
        action={
          primaryAction ? (
            <button
              type="button"
              className="studio-button variant-primary size-md"
              onClick={() => onOpenResumeItem(primaryAction)}
            >
              {/* UI elevation 2026-06-09: map resume-item kinds to human-readable labels */}
              Resume{' '}
              {primaryAction.kind === 'checklist-incomplete'
                ? 'setup'
                : primaryAction.kind || 'work'}{' '}
              →
            </button>
          ) : null
        }
        updated={`Updated ${formatDateLine()}`}
      />

      <LifecycleStrip
        counts={lifecycleCounts}
        blocked={lifecycleBlocked}
        passive={{
          delivered: lifecycleCounts.delivered === 0,
        }}
        substatus={{
          delivered:
            lifecycleCounts.delivered > 0 ? 'measured · learning' : null,
        }}
        onStageClick={(stageId) => onOpenTab(stageId)}
      />

      <SectionDivider
        title="Decide"
        meta="what to ship next"
        right={`${arcDay ? `day ${arcDay}/90` : 'arc pending'} · ${formatDateLine()}`}
      />

      <section className="studio-decision-grid">
        {/* Wave 2.4 — true empty state. When the operator has no
            captures, no drafts, no queue, the previous card showed a
            templated "Choose the next concrete technical point"
            headline with an "Open compose" CTA. That's a placeholder,
            not an action. The studio is now honest: no captures means
            "go capture an idea". The commitment card returns the
            moment any idea / draft / queued / delivered item exists. */}
        {!primaryAction && (ideas?.length || 0) === 0 && posts.length === 0 ? (
          <article className="studio-commitment-card studio-commitment-empty">
            <div className="studio-commitment-kicker">
              <span>Start the day</span>
              <em>capture-first</em>
            </div>
            <h1>Capture an idea to start your arc.</h1>
            <p className="studio-commitment-empty-body">
              The studio runs on captures. A claim, a signal, a debugging trace,
              a client question — anything you'd otherwise lose gets a durable
              home. Once one capture exists, it routes into a draft and the
              daily commitment card takes over.
            </p>
            <div className="studio-commitment-actions">
              <button
                className="studio-console-primary"
                onClick={() => onOpenTab('ideas')}
              >
                Capture an idea<span>↗</span>
              </button>
              <button onClick={() => onOpenTab('truth')}>
                Set up Truth first
              </button>
            </div>
          </article>
        ) : (
          <article className="studio-commitment-card">
            <div className="studio-commitment-kicker">
              <span>
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 10 10"
                  fill="none"
                  aria-hidden="true"
                  style={{
                    display: 'inline-block',
                    verticalAlign: '-1px',
                    marginRight: 8,
                  }}
                >
                  <path
                    d="M2 1.5 L7.5 5 L2 8.5 Z"
                    fill="currentColor"
                    opacity="0.85"
                  />
                </svg>
                Today&apos;s commitment
              </span>
              <em>{primaryAction?.kind || 'compose-ready'}</em>
              <strong>
                01 ·{' '}
                {getPillarName(
                  primaryPost?.pillar || currentPillarId,
                  getPillar
                )}
              </strong>
            </div>
            <h1>
              {primaryAction
                ? primaryAction.title
                : 'Choose the next concrete technical point'}
            </h1>
            <div className="studio-commitment-metrics">
              <div>
                <span>Words</span>
                <strong>
                  {primaryMetrics.value}/{primaryMetrics.max || 'target'}
                </strong>
              </div>
              <div>
                <span>Voice</span>
                <strong className="green">
                  {authorityReview?.voiceScore
                    ? Number(authorityReview.voiceScore).toFixed(2)
                    : 'loaded'}
                </strong>
              </div>
              <div>
                <span>Grounding</span>
                <strong className="green">
                  {latestDurableAuthorityReview ? 'durable' : 'review'}
                </strong>
              </div>
              <div>
                <span>Edited</span>
                <strong>
                  {primaryPost?.updatedAt
                    ? fmtDateTime(primaryPost.updatedAt)
                    : 'not started'}
                </strong>
              </div>
              <div>
                <span>Autonomy</span>
                <strong>
                  {readyIntegrations.length ? 'assist' : 'manual'}
                </strong>
              </div>
            </div>
            <div className="studio-commitment-actions">
              <button
                className="studio-console-primary"
                onClick={() =>
                  primaryAction
                    ? onOpenResumeItem(primaryAction)
                    : onOpenTab('create')
                }
              >
                {primaryAction ? 'Resume draft' : 'Open compose'}
                <span>↗</span>
              </button>
              <button onClick={() => onOpenTab('queue')}>Send to queue</button>
              <button onClick={() => onOpenTab('drafts')}>Skip</button>
            </div>
            <p className="studio-commitment-note">
              {primaryPost?.metadata?.sourceWork?.summary ||
                'Authority compounds when the next draft is tied to proof, not activity.'}
            </p>
          </article>
        )}

        <aside className="studio-stack-panel">
          <div className="studio-panel-head">
            <span>After this · stack</span>
          </div>
          {stackItems.length ? (
            <div className="studio-stack-list">
              {stackItems.map((item) => (
                <button key={item.key} onClick={item.onClick}>
                  <span>{item.number}</span>
                  <div>
                    <strong>{item.title}</strong>
                    <small>{item.meta}</small>
                  </div>
                  <em>
                    {item.Icon ? (
                      <item.Icon
                        width={11}
                        height={11}
                        aria-hidden="true"
                        className="studio-stack-row-icon"
                      />
                    ) : null}
                    {item.action}
                  </em>
                </button>
              ))}
            </div>
          ) : (
            <div className="studio-empty-console">
              No stacked item. Capture a source or schedule the next draft.
            </div>
          )}
        </aside>
      </section>

      <ProgressRail arcDay={arcDay} />

      {/* Wave 24 — autonomy command strip. Sits between Decide and
          Operate so the operator's first scan after "what to ship"
          is "is the loop running". */}
      <SectionDivider
        title="Autonomy"
        meta="what the loop is doing"
        right="kill switch · queue · lifecycle"
      />

      <AutonomyCommandStrip snapshot={autonomySnapshot} onOpenTab={onOpenTab} />

      {/* Wave 27 — engagement insights summary. Surfaces "what's
          working / what's not" without an AutonomyTab dive. */}
      <EngagementInsightsSummary getPillar={getPillar} onOpenTab={onOpenTab} />

      {/* 2026-06-13 — follower-growth gauge (tech-debt P1 item 4). The
          distribution bottleneck finally has a number on the Dashboard. */}
      <FollowerGrowthCard provider="x" />

      <SectionDivider
        title="Operate"
        meta="the machine today"
        right="pipeline · this week"
      />

      <MetricsGrid items={actTwoMetrics} />

      <section className="studio-week-grid">
        {weekCards.map((item) => (
          <WeekCard
            key={item.iso}
            item={item}
            onOpen={() =>
              item.post
                ? onOpenResumeItem({
                    id: item.post.id,
                    title: item.post.topic,
                    actionTab:
                      item.post.status === 'published'
                        ? 'delivered'
                        : item.post.status === 'scheduled'
                          ? 'queue'
                          : 'drafts',
                    targetId: item.post.id,
                  })
                : onOpenTab(item.pillarId ? 'create' : 'calendar')
            }
          />
        ))}
      </section>

      <SectionDivider
        title="Attend"
        meta="pressure & alerts"
        right={`${attentionItems.length} unresolved · last 24h`}
      />

      <section className="studio-attend-grid">
        <article className="studio-console-panel authority">
          <div className="studio-panel-head">
            <span>Authority pressure</span>
            <strong>
              {authorityCoverage?.gapLinkedCaptures?.length || 0} gaps
            </strong>
          </div>
          <p>
            {authority.nextAction ||
              'Run source-fit review before drafting the next public point.'}
          </p>
          <div className="studio-panel-foot">
            <small>
              weak {authorityCoverage?.weakProofCaptures?.length || 0} · topics{' '}
              {authorityCoverage?.pendingTopicCandidates?.length ||
                pendingTopics.length}
            </small>
            <button
              style={btn('ghost')}
              disabled={authorityReviewLoading}
              onClick={() =>
                onRunAuthorityReview?.({
                  scope: 'strategy',
                  platformFamily: 'all',
                })
              }
            >
              {authorityReviewLoading ? 'reviewing' : 'review'}
            </button>
          </div>
        </article>

        <article className="studio-console-panel proof">
          <div className="studio-panel-head">
            <span>Proof health</span>
            <strong>{strongProofAssets} strong</strong>
          </div>
          <dl className="studio-proof-list">
            <div>
              <dt>strong proof</dt>
              <dd>{strongProofAssets}</dd>
            </div>
            <div>
              <dt>unlinked</dt>
              <dd>{unlinkedProofAssets}</dd>
            </div>
            <div>
              <dt>unused</dt>
              <dd>{unusedProofAssets}</dd>
            </div>
          </dl>
          <button
            className="studio-console-link"
            onClick={() => onOpenTab('proof')}
          >
            open proof
          </button>
        </article>

        <article className="studio-console-panel velocity">
          <div className="studio-panel-head">
            <span>Pillar velocity</span>
            <strong>last 30d</strong>
          </div>
          <div className="studio-velocity-list">
            {Object.entries({
              'system-decision':
                authority.pillarBalance['system-decision'] || 0,
              'build-log': authority.pillarBalance['build-log'] || 0,
              'deep-dive': authority.pillarBalance['deep-dive'] || 0,
              'engineering-standards':
                authority.pillarBalance['engineering-standards'] || 0,
            }).map(([key, value]) => (
              <div key={key} className={PILLAR_TONES[key]}>
                <span>{getPillarName(key, getPillar)}</span>
                <i
                  style={{
                    width: `${Math.max(8, Math.min(100, value * 12))}%`,
                  }}
                />
                <strong>{value}</strong>
              </div>
            ))}
          </div>
        </article>
      </section>

      {publishRecoveryItems.length ? (
        <section className="studio-recovery-panel">
          <div className="studio-panel-head">
            <span>Publishing recovery</span>
            <strong>{filteredRecoveryItems.length} visible</strong>
          </div>
          <div className="studio-recovery-filters">
            {[
              ['all', 'All'],
              ['linkedin', 'LinkedIn'],
              ['x', 'X'],
              ['retry-ready', 'Retry ready'],
              ['needs-configuration', 'Config blocked'],
              ['content-blocked', 'Content blocked'],
            ].map(([key, label]) => (
              <button
                key={key}
                className={publishRecoveryFilter === key ? 'active' : ''}
                onClick={() => setPublishRecoveryFilter(key)}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="studio-stack-list compact">
            {filteredRecoveryItems.slice(0, 4).map((item, index) => (
              <button key={item.id} onClick={() => onOpenResumeItem(item)}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <div>
                  <strong>{item.title}</strong>
                  <small>
                    {item.platform} · {item.errorCode} · {item.recoveryState} ·{' '}
                    {fmtDateTime(item.attemptedAt)}
                  </small>
                </div>
                <em>Recover</em>
              </button>
            ))}
          </div>
          {filteredRecoveryItems.length > 4 ? (
            <div className="studio-recovery-overflow">
              {filteredRecoveryItems.length - 4} more failed attempt
              {filteredRecoveryItems.length - 4 === 1 ? '' : 's'} match
              {filteredRecoveryItems.length - 4 === 1 ? 'es' : ''} this filter.
            </div>
          ) : null}
        </section>
      ) : null}

      <section className="studio-activity-log">
        <div className="studio-panel-head">
          <span>Activity log</span>
          <strong>
            <ConsoleBadge tone={failureCount ? 'error' : 'green'}>
              {failureCount} err
            </ConsoleBadge>
            <ConsoleBadge tone="warning">
              {
                runtimeVerifications.filter((item) => item.status === 'failure')
                  .length
              }{' '}
              warn
            </ConsoleBadge>
          </strong>
        </div>
        {activityLog.length ? (
          <div className="studio-log-rows">
            {activityLog.map((item) => (
              <div key={item.id}>
                <span className={item.level.toLowerCase()}>{item.level}</span>
                <small>{item.action}</small>
                <p>{item.detail}</p>
                <time>{item.time}</time>
              </div>
            ))}
          </div>
        ) : (
          <div className="studio-empty-console">
            No publish or verification events have been recorded yet.
          </div>
        )}
      </section>

      <footer className="studio-dashboard-footer">
        <span>Autonomy</span>
        {autonomySnapshot.stateError ? (
          <ConsoleBadge tone="warning">kill-switch ?</ConsoleBadge>
        ) : autonomySnapshot.state?.killSwitchEngaged ? (
          <ConsoleBadge tone="error">kill-switch engaged</ConsoleBadge>
        ) : (
          <ConsoleBadge tone="green">kill-switch off</ConsoleBadge>
        )}
        {FOOTER_MODE_TYPES.map((entry) => {
          const policy = autonomySnapshot.policiesByContentType[entry.key]
          const mode = policy?.autonomyMode || '—'
          return (
            <span key={entry.key} title={`${entry.key} · ${mode}`}>
              {entry.short} {mode}
            </span>
          )
        })}
        <strong>
          integrations {readyIntegrations.length}/{integrationEntries.length}
        </strong>
        <span>
          verifications{' '}
          {
            runtimeVerifications.filter((item) => item.status === 'success')
              .length
          }
          /{runtimeVerifications.length}
        </span>
        <button onClick={() => onOpenTab('settings')}>⌘K command</button>
      </footer>
    </div>
  )
}
